import { useEffect, useRef, useState, type ReactNode } from 'react';
import { css, cx } from 'styled-system/css';

interface RevealProps {
  children: ReactNode;
  delay?: number;
  className?: string;
}

export function Reveal({ children, delay = 0, className }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -5% 0px' },
    );
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      data-visible={isVisible || undefined}
      style={{ transitionDelay: `${delay}ms` }}
      className={cx(
        css({
          opacity: 0,
          transform: 'translateY(12px)',
          transition:
            'opacity 600ms {easings.out}, transform 600ms {easings.out}',
          _motionReduce: {
            opacity: 1,
            transform: 'none',
            transition: 'none',
          },
          '&[data-visible]': { opacity: 1, transform: 'none' },
        }),
        className,
      )}
    >
      {children}
    </div>
  );
}
